import React, { Dispatch, SetStateAction } from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, Icons } from '@components/common';
import Actions from '@components/common/Actions';

import { dateFormattingWithSlash } from '@utils/date';
import { getDateWithoutOffset } from '@utils/kpi-utils/date';

import { IMavenirConfig, IMavenirConfigInfo } from '@interfaces/cmModule/dataSources';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ITableDatepickerFilter, TTableFilter } from '@interfaces/components/table';

import { columnSizes } from '@constants/table';

import LocalIdsListCell from './LocalIdsListCell';

import styles from '@components/common/Table/Table.module.scss';

const MIN_FILTER_WIDTH = '200px';

export const getDateFilters = ({
	date,
	onChangeDate,
	datesOptions,
}: {
	onChangeDate: (value: number | null) => void;
	date: Date | null;
	datesOptions: Date[];
}): Array<ITableDatepickerFilter> => {
	return [
		{
			name: 'date',
			label: 'Date',
			handleChange: onChangeDate,
			selected: date,
			includeDatesFrom: datesOptions,
		},
	];
};

export const getFilters = ({
	setSelectedConfigType,
	setSelectedConfigVersion,
	setSelectedConfigId,
	typeOptions = [],
	versionOptions = [],
	idsOptions = [],
	selectedConfigType,
	selectedConfigVersion,
	selectedConfigId,
	configId,
	type,
	version,
}: {
	setSelectedConfigType: Dispatch<SetStateAction<TSelectOption<string>>>;
	setSelectedConfigVersion: Dispatch<SetStateAction<TSelectOption<string>>>;
	setSelectedConfigId: Dispatch<SetStateAction<TSelectOption<string>>>;
	selectedConfigType: TSelectOption;
	selectedConfigVersion: TSelectOption;
	selectedConfigId: TSelectOption;
	typeOptions: TSelectOption<string>[];
	versionOptions: TSelectOption<string>[];
	idsOptions: TSelectOption<string>[];
	configId: string | null;
	type: string | null;
	gnbId: string | null;
	version: string | null;
}): Array<TTableFilter> => {
	return [

		{
			name: 'version',
			placeholder: 'Release version',
			option: selectedConfigVersion,
			options: versionOptions,
			minWidth: MIN_FILTER_WIDTH,
			setState: setSelectedConfigVersion,
			defaultValue: version,
			isFilterStyle: false,
		},
		{
			name: 'type',
			placeholder: 'Config type',
			option: selectedConfigType,
			options: typeOptions,
			minWidth: MIN_FILTER_WIDTH,
			setState: setSelectedConfigType,
			defaultValue: type,
		},
		{
			name: 'Config id',
			placeholder: 'Config ID',
			option: selectedConfigId,
			options: idsOptions,
			minWidth: MIN_FILTER_WIDTH,
			setState: setSelectedConfigId,
			defaultValue: configId,
		},
	];
};

const MAX_CELL_ID_LIST_LENGTH = 15;

export const getColumns = ({
	setSelectedConfig,
	openConfigTree,
	openDeleteConfirm,
	setLocalIdsList,
	handleExportConfig,
}: {
	setSelectedConfig: (config: IMavenirConfigInfo) => void;
	openConfigTree: () => void;
	openDeleteConfirm: () => void;
	setLocalIdsList: (idsList: number[]) => void;
	handleExportConfig: (id?: string) => void;
}) => {
	return [
		{
			id: 'expander',
			size: columnSizes.size35,
			header: '',
			cell: ({ row }: CellContext<any, any>) => {
				return (
					row.getCanExpand() ? (
						<span
							className={styles.expandAction}
							onClick={row.getToggleExpandedHandler()}
						>
						{row.getIsExpanded() ? Icons.chevronUp() : Icons.chevronDown()}
					</span>
					) : null
				);
			},
		},
		{
			header: 'GNBID',
			accessorKey: 'gnbId',
			cell: ({ getValue }: CellContext<IMavenirConfig, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'Release version',
			accessorKey: 'version',
			cell: ({ getValue }: CellContext<IMavenirConfig, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'Config type',
			accessorKey: 'type',
			cell: ({ getValue }: CellContext<IMavenirConfig, any>) => {
				const text = getValue()?.title;

				return <AdaptiveTableCell text={text} />;
			},
		},
		{
			header: 'Config id',
			accessorKey: 'configId',
			cell: ({ getValue }: CellContext<IMavenirConfig, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'Cell Local ID List',
			accessorKey: 'localCellIds',
			cell: ({ getValue }: CellContext<IMavenirConfig, any>) => {
				const value = getValue();

				const onViewHandler = () => setLocalIdsList(value || []);

				return (
					<LocalIdsListCell
						idsList={value || []}
						maxLength={MAX_CELL_ID_LIST_LENGTH}
						onViewHandler={onViewHandler}
					/>
				);
			},
		},
		{
			header: 'Date and Time',
			accessorKey: 'createdAt',
			cell: ({ getValue }: CellContext<IMavenirConfig, any>) => {
				const value = getValue();
				const dateWithoutOffset = getDateWithoutOffset(value);
				const date = dateFormattingWithSlash(dateWithoutOffset);

				return value && <AdaptiveTableCell text={date} />;
			},
		},
		{
			header: 'Actions',
			accessorKey: 'actions',
			enableSorting: false,
			size: columnSizes.size90,
			cell: ({ row: { original } }: CellContext<IMavenirConfigInfo, any>) => {
				const selectHandler = () => setSelectedConfig(original);

				const viewHandler = () => {
					selectHandler();
					openConfigTree();
				};

				const deleteHandler = () => {
					selectHandler();
					openDeleteConfirm();
				};

				const downloadHandler = () => {
					handleExportConfig(original?.id);
				};

				return (
					original.configId ? (
							<Actions
								deleteTooltip='Delete'
								downloadHandler={downloadHandler}
								downloadTooltip='Export'
								handleDelete={deleteHandler}
								isCustomAction
								isDelete
								isDownload
								isView
								viewHandler={viewHandler}
								viewTooltip='View'
							/>
						)
						: null
				);
			},
		},
	];
};
